// UXCam Web SDK wrapper.
//
// The SDK ships as an inline stub (`window.uxc`) that queues calls in `__t`
// until the remote recorder script loads and replays them, so everything here
// is safe to call from the first tick of app boot - including before login.
//
// Every export is a no-op when `VUE_APP_UXCAM_KEY` is unset (local dev, unit
// tests) and every call is wrapped: analytics must never be able to break a
// membership submission.
import { warn } from "@/utils/logger";

const APP_KEY = process.env.VUE_APP_UXCAM_KEY;
const SDK_SRC = "https://websdk-recording.uxcam.com/index.js";
const SCRIPT_ID = "uxcam-web-sdk";

// Only the last value written for a key is kept, so re-sending an unchanged
// property on every watcher tick is pure noise. Cache what we have already set.
const sentProperties = Object.create(null);

// UXCam stores at most 20 user properties per user. Past that it drops the
// overflow and replaces it with a `_UXCam_Overload` marker, so which properties
// survive becomes arbitrary. Refuse new keys beyond the cap instead: the first
// 20 claimed stay stable and the loss is visible in the console.
const MAX_USER_PROPERTIES = 20;
let warnedAboutCap = false;

let started = false;

function client() {
  return typeof window !== "undefined" ? window.uxc : null;
}

//
// Install the stub queue + inject the recorder script. Idempotent.
//
export function initUxcam() {
  if (started) return;
  if (!APP_KEY) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;

  started = true;

  try {
    const opts = {};
    if (process.env.VUE_APP_VERSION) {
      opts.appVersion = process.env.VUE_APP_VERSION;
    }

    window.uxc = {
      __t: [],
      __ak: APP_KEY,
      __o: opts,
      event: function (n, p) {
        this.__t.push(["event", n, p]);
      },
      setUserIdentity: function (i) {
        this.__t.push(["setUserIdentity", i]);
      },
      setUserProperty: function (k, v) {
        this.__t.push(["setUserProperty", k, v]);
      },
      setUserProperties: function (p) {
        this.__t.push(["setUserProperties", p]);
      },
    };

    if (document.getElementById(SCRIPT_ID)) return;

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = SDK_SRC;
    script.async = true;
    script.defer = true;
    script.id = SCRIPT_ID;
    script.crossOrigin = "anonymous";
    document.head.appendChild(script);

    setUxcamProperties({ environment: process.env.VUE_APP_ENV || "dev" });
  } catch (e) {
    warn("UXCam init failed:", e);
  }
}

//
// Tie the session to the signed-in Sea Tow employee.
//
export function identifyUxcamUser(account) {
  if (!account) return;
  const uxc = client();
  if (!uxc) return;

  try {
    const identity = account.username || account.localAccountId;
    if (identity) {
      uxc.setUserIdentity(identity);
    }
    // `setUserIdentity` already carries the email as the custom user ID, so
    // only the display name earns one of the 20 property slots.
    setUxcamProperties({ agent_name: account.name });
  } catch (e) {
    warn("UXCam identify failed:", e);
  }
}

//
// Set user properties, skipping keys whose value has not changed and keys with
// nothing to say (undefined / empty string). `null` is sent through so a
// cleared field visibly clears on the session.
//
export function setUxcamProperties(properties) {
  const uxc = client();
  if (!uxc || !properties) return;

  try {
    const changed = {};
    let hasChanges = false;

    Object.keys(properties).forEach((key) => {
      let value = properties[key];
      if (value === undefined || value === "") value = null;
      if (sentProperties[key] === value) return;

      const isNewKey = !(key in sentProperties);
      if (isNewKey && Object.keys(sentProperties).length >= MAX_USER_PROPERTIES) {
        if (!warnedAboutCap) {
          warnedAboutCap = true;
          warn(
            `UXCam user property limit (${MAX_USER_PROPERTIES}) reached; ` +
              `dropping "${key}" and any further new keys. Send it as an event ` +
              `property instead.`
          );
        }
        return;
      }

      sentProperties[key] = value;
      changed[key] = value;
      hasChanges = true;
    });

    if (hasChanges) {
      uxc.setUserProperties(changed);
    }
  } catch (e) {
    warn("UXCam setUserProperties failed:", e);
  }
}

//
// Log a custom event. Property values are flattened to JSON-safe scalars.
//
export function trackUxcamEvent(name, properties) {
  const uxc = client();
  if (!uxc || !name) return;

  try {
    uxc.event(name, sanitize(properties));
  } catch (e) {
    warn(`UXCam event "${name}" failed:`, e);
  }
}

//
// Errors get their own entry point so every failure lands on one event name
// and stays filterable by `source` in the UXCam dashboard.
//
export function trackUxcamError(source, error, extra) {
  const message =
    (error && (error.message || error.toString())) || "Unknown error";

  trackUxcamEvent("app_error", {
    source,
    message: truncate(message, 300),
    ...(extra || {}),
  });
}

function sanitize(properties) {
  if (!properties) return undefined;

  const out = {};
  Object.keys(properties).forEach((key) => {
    const value = properties[key];
    if (value === undefined || value === null) return;
    if (typeof value === "string") {
      out[key] = truncate(value, 300);
    } else if (typeof value === "number" || typeof value === "boolean") {
      out[key] = value;
    } else {
      out[key] = truncate(String(value), 300);
    }
  });
  return out;
}

function truncate(value, max) {
  return value.length > max ? `${value.slice(0, max)}...` : value;
}
